import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/react';
import { Link, useOutletContext } from 'react-router-dom';
import ConfidenceBadge from '../components/ConfidenceBadge';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.horizontl.io';

const PLAN_LIMITS = {
  trial: 10,
  support_essential: 150,
};

const STATUS_LABELS = {
  approved_unchanged: 'accepted · initial draft',
  edited_and_sent: 'accepted · amended',
  rejected: 'rejected',
  draft_saved: 'draft edited',
  delivery_failed: 'delivery issue',
  not_applicable: 'autonomous',
};

const isTestTicket = ticket => Boolean(
  ticket.is_onboarding_test
  || ticket.is_configuration_test
  || ticket.delivery_status === 'simulation_only'
);

const formatPercentage = (part, total) => (
  total > 0 ? `${Math.round((part / total) * 100)}%` : '0%'
);

const formatDuration = seconds => {
  if (seconds == null) return 'Not yet measured';
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes}m ${total % 60}s`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
};

export default function Home() {
  const { getToken } = useAuth();
  const { org, tickets } = useOutletContext();
  const [upgradeState, setUpgradeState] = useState({ loading: false, error: '' });
  const [reviewMetrics, setReviewMetrics] = useState(null);

  useEffect(() => {
    let active = true;
    const loadReviewMetrics = async () => {
      try {
        const token = await getToken();
        const response = await fetch(`${API_URL}/api/review-queue/metrics`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json().catch(() => ({}));
        if (response.ok && active) setReviewMetrics(data);
      } catch (_) {
        // The existing ticket-derived view remains as a safe fallback.
      }
    };
    loadReviewMetrics();
    return () => { active = false; };
  }, [getToken, tickets]);

  const dashboard = useMemo(() => {
    const liveTickets = tickets.filter(ticket => !isTestTicket(ticket));
    const total = liveTickets.length;
    const resolved = liveTickets.filter(ticket => ticket.resolved_at).length;
    const approved = liveTickets.filter(ticket => ticket.review_status === 'approved_unchanged').length;
    const edited = liveTickets.filter(ticket => ticket.review_status === 'edited_and_sent').length;
    const rejected = liveTickets.filter(ticket => ticket.review_status === 'rejected').length;
    const accepted = approved + edited;
    const reviewed = approved + edited + rejected;
    const changePercentages = liveTickets
      .filter(ticket => ['approved_unchanged', 'edited_and_sent'].includes(ticket.review_status))
      .map(ticket => Number(ticket.change_percentage || 0))
      .filter(value => Number.isFinite(value));

    return {
      liveTickets,
      total,
      resolved,
      resolutionRate: formatPercentage(resolved, total),
      acceptanceRate: formatPercentage(accepted, reviewed),
      confidence: {
        high: liveTickets.filter(ticket => ticket.confidence === 'high').length,
        medium: liveTickets.filter(ticket => ticket.confidence === 'medium').length,
        low: liveTickets.filter(ticket => ticket.confidence === 'low').length,
      },
      performance: {
        accepted,
        approved,
        edited,
        rejected,
        pending: liveTickets.filter(ticket => ['pending', 'draft_saved', 'delivery_failed'].includes(ticket.review_status || 'pending')).length,
        averageChange: changePercentages.length
          ? `${Math.round(changePercentages.reduce((sum, value) => sum + value, 0) / changePercentages.length)}%`
          : '—',
        averageRetained: changePercentages.length
          ? `${Math.round(100 - (changePercentages.reduce((sum, value) => sum + value, 0) / changePercentages.length))}%`
          : '—',
        deliverySuccess: accepted
          ? formatPercentage(liveTickets.filter(ticket => (
            ['approved_unchanged', 'edited_and_sent'].includes(ticket.review_status)
            && ticket.delivery_status === 'sent'
          )).length, accepted)
          : '—',
      },
    };
  }, [tickets]);

  const totalTickets = reviewMetrics?.total_tickets ?? dashboard.total;
  const planLimit = PLAN_LIMITS[org.org_type];
  const usageWarning = planLimit && totalTickets >= planLimit * 0.8;
  const usageExhausted = planLimit && totalTickets >= planLimit;
  const performance = reviewMetrics ? {
    accepted: reviewMetrics.accepted ?? ((reviewMetrics.sent_unchanged || 0) + (reviewMetrics.edited_and_sent || 0)),
    approved: reviewMetrics.sent_unchanged,
    edited: reviewMetrics.edited_and_sent,
    rejected: reviewMetrics.rejected,
    pending: reviewMetrics.no_feedback ?? reviewMetrics.pending_review,
    averageChange: reviewMetrics.average_change_percentage == null
      ? '—' : `${Math.round(reviewMetrics.average_change_percentage)}%`,
    averageRetained: reviewMetrics.average_retained_percentage == null
      ? '—' : `${Math.round(reviewMetrics.average_retained_percentage)}%`,
    deliverySuccess: `${reviewMetrics.review_delivery_success_rate ?? 0}%`,
  } : dashboard.performance;
  const acceptanceRate = reviewMetrics
    ? `${reviewMetrics.acceptance_rate ?? 0}%` : dashboard.acceptanceRate;
  const effectivenessRate = reviewMetrics?.first_response_effectiveness_rate == null
    ? 'Not yet measured' : `${reviewMetrics.first_response_effectiveness_rate}%`;

  const openUpgrade = async () => {
    if (upgradeState.loading) return;
    setUpgradeState({ loading: true, error: '' });

    try {
      const token = await getToken();
      const plan = org.org_type === 'trial' ? 'support_essential' : 'super_support';
      const response = await fetch(`${API_URL}/api/create-upgrade-session`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ plan }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.checkout_url) {
        throw new Error(data.detail || 'Unable to open secure billing.');
      }
      window.location.assign(data.checkout_url);
    } catch (error) {
      setUpgradeState({ loading: false, error: error.message || 'Unable to open secure billing.' });
    }
  };

  return (
    <div className="page-stack dashboard-page">
      {planLimit && (
        <section className={`usage-banner ${usageWarning ? 'warning' : ''}`}>
          <div>
            <strong>{usageExhausted ? 'Ticket limit reached' : `${totalTickets} of ${planLimit} tickets used`}</strong>
            <span>{usageExhausted ? 'Upgrade to continue processing tickets.' : `${planLimit - totalTickets} tickets remaining.`}</span>
          </div>
          <button className="primary-button" type="button" onClick={openUpgrade} disabled={upgradeState.loading}>
            {upgradeState.loading ? 'Opening billing…' : org.org_type === 'trial' ? 'Start plan' : 'Upgrade plan'}
          </button>
        </section>
      )}

      {upgradeState.error && <div className="error-banner">{upgradeState.error}</div>}

      <section className="metric-grid" aria-label="Workspace metrics">
        <article className="metric-card"><span>Median first response</span><strong>{formatDuration(reviewMetrics?.median_first_response_seconds)}</strong><small>{reviewMetrics?.timing_sample || 0} measured tickets</small></article>
        <article className="metric-card"><span>Median resolution time</span><strong>{formatDuration(reviewMetrics?.median_resolution_seconds)}</strong><small>{reviewMetrics?.resolved_tickets || 0} resolved tickets</small></article>
        <article className="metric-card"><span>First-response effectiveness</span><strong>{effectivenessRate}</strong><small>Resolved within one customer follow-up</small></article>
        <article className="metric-card"><span>Human acceptance</span><strong>{acceptanceRate}</strong><small>{reviewMetrics?.explicitly_reviewed ?? (performance.accepted + performance.rejected)} explicit reviews</small></article>
      </section>

      <section className="panel kpi-section">
        <div className="panel-heading"><div><h2>Where ticket time is spent</h2><p>Measured from Jira timestamps and statuses. Other workflow time is not labelled as human working time.</p></div><strong className="sample-label">{reviewMetrics?.timing_sample || 0} measured</strong></div>
        <div className="operational-metric-grid">
          <article><span className="kpi-dot dana" /><div><small>Median Dana processing</small><strong>{formatDuration(reviewMetrics?.median_dana_seconds)}</strong></div></article>
          <article><span className="kpi-dot customer" /><div><small>Median waiting for customer</small><strong>{formatDuration(reviewMetrics?.median_customer_wait_seconds)}</strong></div></article>
          <article><span className="kpi-dot workflow" /><div><small>Median other workflow time</small><strong>{formatDuration(reviewMetrics?.median_other_workflow_seconds)}</strong></div></article>
          <article><span className="kpi-dot delivery" /><div><small>Delivery success</small><strong>{performance.deliverySuccess}</strong></div></article>
        </div>
      </section>

      <section className="analytics-grid">
        <article className="panel">
          <div className="panel-heading"><div><h2>Confidence breakdown</h2><p>Live responses by Dana confidence level.</p></div></div>
          <dl className="stat-list">
            <div><dt>80–100% confidence</dt><dd className="high-value">{dashboard.confidence.high} tickets</dd></div>
            <div><dt>55–79% confidence</dt><dd className="medium-value">{dashboard.confidence.medium} tickets</dd></div>
            <div><dt>0–54% confidence</dt><dd className="low-value">{dashboard.confidence.low} tickets</dd></div>
          </dl>
        </article>

        <article className="panel">
          <div className="panel-heading"><div><h2>Response quality</h2><p>Explicit human decisions and substantive editing.</p></div></div>
          <dl className="stat-list">
            <div><dt>Accepted unchanged or trivial edits</dt><dd>{performance.approved}</dd></div>
            <div><dt>Accepted after meaningful edits</dt><dd>{performance.edited}</dd></div>
            <div><dt>Explicitly rejected</dt><dd>{performance.rejected}</dd></div>
            <div><dt>No feedback provided</dt><dd>{performance.pending}</dd></div>
            <div><dt>Average response retained</dt><dd>{performance.averageRetained}</dd></div>
            <div><dt>Tickets reopened</dt><dd>{reviewMetrics?.reopened_tickets || 0}</dd></div>
          </dl>
        </article>
      </section>

      <section className="analytics-grid">
        <article className="panel">
          <div className="panel-heading"><div><h2>Resolution outcomes</h2><p>Based on actual Jira resolution events, not Dana’s prediction.</p></div></div>
          <dl className="stat-list">
            <div><dt>Resolved tickets</dt><dd>{reviewMetrics?.resolved_tickets || 0}</dd></div>
            <div><dt>Resolved without customer follow-up</dt><dd>{reviewMetrics?.one_touch_resolutions || 0}</dd></div>
            <div><dt>Resolved within one customer follow-up</dt><dd>{reviewMetrics?.one_followup_resolutions || 0}</dd></div>
            <div><dt>Effectiveness sample</dt><dd>{reviewMetrics?.effectiveness_sample || 0}</dd></div>
          </dl>
        </article>
        <article className="panel">
          <div className="panel-heading"><div><h2>Why drafts were rejected</h2><p>Structured TSE feedback from completed reviews.</p></div></div>
          {reviewMetrics?.rejection_reasons?.length ? <dl className="stat-list">{reviewMetrics.rejection_reasons.map(reason => <div key={reason.code}><dt>{reason.code.replaceAll('_', ' ')}</dt><dd>{reason.count}</dd></div>)}</dl> : <div className="compact-empty-state">No structured rejection feedback yet.</div>}
        </article>
      </section>

      <section className="panel table-panel dashboard-table">
        <div className="panel-heading table-heading"><div><h2>Recent tickets</h2><p>Live ticket activity. Configuration tests are excluded.</p></div><Link to="/tickets">View all tickets</Link></div>
        <table className="data-table">
          <thead><tr><th>Ticket</th><th>Summary</th><th>Confidence</th><th>Resolved</th><th>TSE action</th><th>Date</th></tr></thead>
          <tbody>
            {dashboard.liveTickets.slice(0, 8).map(ticket => {
              return (
                <tr key={ticket.id}>
                  <td><Link to={`/tickets?highlight=${encodeURIComponent(ticket.id)}`}>{ticket.ticket_key}</Link></td>
                  <td>{ticket.summary || '—'}</td>
                  <td><ConfidenceBadge ticket={ticket} /></td>
                  <td>{ticket.resolved_at ? 'Yes' : ticket.jira_status || 'Open'}</td>
                  <td><span className="activity-chip">{STATUS_LABELS[ticket.review_status] || ticket.review_status || 'pending'}</span></td>
                  <td>{ticket.created_at ? new Date(ticket.created_at).toLocaleDateString('en-GB') : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!dashboard.liveTickets.length && <div className="empty-state">No live tickets yet. Connect a ticketing system to get started.</div>}
      </section>
    </div>
  );
}
